import {
    faAnalytics,
    faChartLine,
    faChartScatter,
    faCompass,
    faExclamationTriangle,
    faMapMarkedAlt,
    faPhotoVideo,
    faTimes,
} from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip } from "antd";
import { windowFeaturesString as railDetailWidgetWindowFeaturesString } from "components/Widgets/RailDetailWidget";
import { ReactComponent as RailSectionIcon } from "icons/rail-section.svg";
import _ from "lodash";
import React, { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { closeWidget, openWidget } from "redux/actions/widgetActions";
import OBCSpinner from "./util/OBC";

const sidekickTypesSelector = (state) => {
    const currentDashboard = _.find(state.dashboards, (dash) => dash.access_token === state.access_token);
    return _.get(currentDashboard, ["config", "sidekick_widget_types"], []);
};

const userDetailsSelector = (state) => state.userDetails;
const railImageConfigObjectSelector = (state) => state.railInspection.railInspectionImageConfig;
const sessionTagsSelector = (state) => _.get(state.sessions, [state.playlist.data.routeID, "tags"], []);
const widgetsSelector = (state) => state.widgets;

const SidekickPopoutContent = ({ showToolTips }) => {
    const sidekickTypes = useSelector(sidekickTypesSelector);
    const userDetails = useSelector(userDetailsSelector);
    const railImageConfigObject = useSelector(railImageConfigObjectSelector);
    const widgets = useSelector(widgetsSelector);
    const sessionTags = useSelector(sessionTagsSelector);

    const dispatch = useDispatch();

    const makeStyle = useCallback(
        (widget) => {
            const possibleKeys = Object.keys(widgets[widget] || {});
            const isTrue = possibleKeys.some((key) => widgets[widget][key] === true);
            return { backgroundColor: possibleKeys.length ? (isTrue ? "#4c527c" : "#42486d") : "" };
        },
        [widgets],
    );

    const openWidgets = useMemo(() => Object.keys(widgets).filter((key) => Object.keys(widgets[key]).length > 0), [widgets]);

    const hasDetailImages = useMemo(() => {
        return _.get(railImageConfigObject, "inspection_images", false)
            ? !!railImageConfigObject.inspection_images.filter((config) => config.showInModal).length
            : false;
    }, [railImageConfigObject]);

    return (
        <div style={{ position: "relative", width: 260 }}>
            {(sidekickTypes.includes("Map") || openWidgets.includes("map")) && (
                <div
                    className="UserDetailsPopupButton"
                    onClick={() => dispatch(openWidget("map"))}
                    style={makeStyle("map")}>
                    <FontAwesomeIcon
                        icon={faMapMarkedAlt}
                        style={{ fontSize: 28, color: "#e8dfff" }}
                    />
                    <div className={"UserDetailsPopupButtonText"}>Map</div>
                    <Close widgetType={"map"} />
                </div>
            )}
            {((sidekickTypes.includes("Detail Rail") && hasDetailImages) || openWidgets.includes("rail-detail")) && (
                <div
                    className="UserDetailsPopupButton"
                    onClick={() =>
                        dispatch(
                            openWidget("rail-detail", {
                                features: railDetailWidgetWindowFeaturesString,
                            }),
                        )
                    }
                    style={makeStyle("rail-detail")}>
                    <RailSectionIcon style={{ color: "#e8dfff" }} />
                    <div
                        className={"UserDetailsPopupButtonText"}
                        id="DetailedRailButton">
                        Detailed Rail Images
                    </div>
                    <Close
                        widgetType={"rail-detail"}
                        humanName={"rail detail"}
                    />
                </div>
            )}
            {((sidekickTypes.includes("Track Geometry") && sessionTags.includes("Track Geometry")) || openWidgets.includes("track-geom-v2")) && (
                <div
                    className="UserDetailsPopupButton"
                    onClick={() => dispatch(openWidget("track-geom-v2"))}
                    style={makeStyle("track-geom-v2")}>
                    <FontAwesomeIcon
                        icon={faChartLine}
                        style={{ fontSize: 28, color: "#e8dfff" }}
                    />
                    <div className={"UserDetailsPopupButtonText"}>Track Geometry</div>
                    <Close
                        widgetType={"track-geom-v2"}
                        humanName={"track geometry"}
                    />
                </div>
            )}
            {((sidekickTypes.includes("OLE Geometry") && sessionTags.includes("OLE Geometry")) || openWidgets.includes("ole-geometry")) && (
                <div
                    className="UserDetailsPopupButton"
                    onClick={() => dispatch(openWidget("ole-geometry"))}
                    style={makeStyle("ole-geometry")}>
                    <FontAwesomeIcon
                        icon={faChartScatter}
                        style={{ fontSize: 28, color: "#e8dfff" }}
                    />
                    <div className={"UserDetailsPopupButtonText"}>OLE Geometry</div>
                    <Close
                        widgetType={"ole-geometry"}
                        humanName={"OLE Geometry"}
                    />
                </div>
            )}
            {((sidekickTypes.includes("Conductor Shoe Geometry") && sessionTags.includes("Conductor Shoe Geometry")) ||
                openWidgets.includes("conductor-shoe-geometry") ||
                userDetails.userConfig.super_admin) && ( // TODO: remove once Shoecam tags have been added to sessions
                <div
                    className="UserDetailsPopupButton"
                    onClick={() => dispatch(openWidget("conductor-shoe-geometry"))}
                    style={makeStyle("conductor-shoe-geometry")}>
                    <FontAwesomeIcon
                        icon={faChartScatter}
                        style={{ fontSize: 28, color: "#e8dfff" }}
                    />
                    <div className={"UserDetailsPopupButtonText"}>Conductor Shoe Geometry</div>
                    <Close
                        widgetType={"conductor-shoe-geometry"}
                        humanName={"Conductor Shoe Geometry"}
                    />
                </div>
            )}
            {(sidekickTypes.includes("Stills Player") || openWidgets.includes("stills")) && (
                <div
                    className="UserDetailsPopupButton"
                    onClick={() => dispatch(openWidget("stills"))}
                    style={makeStyle("stills")}>
                    <FontAwesomeIcon
                        icon={faPhotoVideo}
                        style={{ fontSize: 28, color: "#e8dfff" }}
                    />
                    <div className={"UserDetailsPopupButtonText"}>Stills Player</div>
                    <Close widgetType={"stills"} />
                </div>
            )}
            {(sidekickTypes.includes("Location Information") || openWidgets.includes("location")) && (
                <div
                    className="UserDetailsPopupButton"
                    onClick={() => dispatch(openWidget("location"))}
                    style={makeStyle("location")}>
                    <FontAwesomeIcon
                        icon={faCompass}
                        style={{ fontSize: 28, color: "#e8dfff" }}
                    />
                    <div className={"UserDetailsPopupButtonText"}>Location Information</div>
                    <Close
                        widgetType={"location"}
                        humanName={"location information"}
                    />
                </div>
            )}
            {(sidekickTypes.includes("Exceedances") || openWidgets.includes("exceedences")) && (
                <div
                    className="UserDetailsPopupButton"
                    onClick={() => dispatch(openWidget("exceedences"))}
                    style={makeStyle("exceedences")}>
                    <FontAwesomeIcon
                        icon={faExclamationTriangle}
                        style={{ fontSize: 28, color: "#e8dfff" }}
                    />
                    <div className={"UserDetailsPopupButtonText"}>Exceedances</div>
                    <Close widgetType={"exceedences"} />
                </div>
            )}
            {userDetails.userConfig.super_admin && (
                <div
                    className="UserDetailsPopupButton"
                    onClick={() => dispatch(openWidget("timeline"))}
                    style={makeStyle("timeline")}>
                    <FontAwesomeIcon
                        icon={faAnalytics}
                        style={{ fontSize: 28, color: "#e8dfff" }}
                    />
                    <div className={"UserDetailsPopupButtonText"}>Timeline View</div>
                    <Close widgetType={"timeline"} />
                </div>
            )}
        </div>
    );
};

const Close = ({ widgetType, humanName, showToolTips }) => {
    const dispatch = useDispatch();
    const widgets = useSelector((state) => _.get(state.widgets, widgetType, {}));

    const existingWidgetIds = Object.keys(widgets);
    const showButton = existingWidgetIds.some((key) => widgets[key] === true);

    return existingWidgetIds.length ? (
        showButton ? (
            <Tooltip
                title={showToolTips ? `Close ${humanName ?? widgetType} widget` : ""}
                style={{ zIndex: 999999 }}>
                <div
                    className="CloseButton"
                    onClick={(e) => {
                        e.stopPropagation();
                        dispatch(closeWidget(widgetType));
                    }}>
                    <span className="default">
                        {existingWidgetIds.length > 1 ? (
                            <span className="CloseButtonCount">{existingWidgetIds.length}</span>
                        ) : (
                            <FontAwesomeIcon icon={faTimes} />
                        )}
                    </span>
                    <span className="hovered">
                        <FontAwesomeIcon icon={faTimes} />
                    </span>
                </div>
            </Tooltip>
        ) : (
            <OBCSpinner
                size={22}
                color={"#e8dfff"}
            />
        )
    ) : (
        <></>
    );
};

export default SidekickPopoutContent;
